/**
 * StarterKitHub - Card-grid directory page for all pre-loaded Starter Kit content
 *
 * Displays 7 category cards in a responsive CSS Grid layout. Cards either
 * navigate to a sub-page (linkTo), expand an accordion of sub-links (subLinks),
 * or display an informational "Runs automatically" tag (infoOnly).
 *
 * Only one accordion card can be expanded at a time.
 *
 * @route /starter-kit
 * @version 1.0.0
 * @date 2026-03-26
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BRANDING } from '../config/branding';
import './StarterKitHub.css';

// ============================================================================
// TYPES
// ============================================================================

interface SubLink {
  label: string;
  count: number;
  to: string;
}

interface StarterKitCard {
  id: string;
  title: string;
  count: string;
  description: string;
  linkTo?: string;
  subLinks?: SubLink[];
  infoOnly?: boolean;
}

// ============================================================================
// CARD DATA
// ============================================================================

const STARTER_KIT_CARDS: StarterKitCard[] = [
  {
    id: 'governance-templates',
    title: 'Governance Templates',
    count: '22 templates',
    description:
      'RACI matrices, policy documents, SOPs, and use case templates with regulatory citations and fill-in-the-blank variables.',
    subLinks: [
      { label: 'RACI Templates', count: 7, to: '/starter-kit/raci-templates' },
      { label: 'Policy Templates', count: 5, to: '/starter-kit/policy-templates' },
      { label: 'SOP Templates', count: 5, to: '/starter-kit/sops' },
      { label: 'Use Case Templates', count: 5, to: '/starter-kit/use-cases' },
    ],
  },
  {
    id: 'reference-ontology',
    title: 'Reference Ontology',
    count: '1 ontology',
    description:
      'A pharma-grade reference ontology for benchmarking structural health, SHACL validation, and FAIR alignment.',
    linkTo: '/ontology',
  },
  {
    id: 'fair-playbooks',
    title: 'FAIR Remediation Playbooks',
    count: '15 playbooks',
    description:
      'Step-by-step remediation guides for every FAIR dimension, with SPARQL snippets and before/after examples.',
    linkTo: '/starter-kit/playbooks',
  },
  {
    id: 'curated-literature',
    title: 'Curated Literature',
    count: '63 references',
    description:
      'Peer-reviewed papers, W3C specs, and regulatory guidance documents powering Bridge AI recommendations.',
    // DC-1645: literature citations are configured under AI Preferences
    linkTo: '/settings/ai-preferences',
  },
  {
    id: 'privacy-detection',
    title: 'Privacy Detection',
    count: '29 patterns',
    description:
      'Regex and heuristic patterns for PII, PHI, and sensitive data detection across structured and free-text columns.',
    infoOnly: true,
  },
  {
    id: 'governance-personas',
    title: 'Governance Personas',
    count: '21 roles',
    description:
      'Pre-defined organizational roles for RACI assignment, from Data Steward to Chief Data Officer.',
    // DC-1648/DC-1645: the persona surface lives under People
    linkTo: '/settings/team/personas',
  },
  {
    id: 'assessment-profiles',
    title: 'Assessment Profiles',
    count: '5 profiles',
    description:
      'Domain-tuned assessment profiles for genomics, clinical, sensor, financial, and commercial data.',
    infoOnly: true,
  },
];

// ============================================================================
// STARTERKIT HUB COMPONENT
// ============================================================================

export const StarterKitHub: React.FC = () => {
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  const handleToggleExpand = (cardId: string) => {
    setExpandedCard((prev) => (prev === cardId ? null : cardId));
  };

  return (
    <div className="starter-kit-hub">
      {/* Back link */}
      <Link to="/home" className="starter-kit-back-link">
        <span className="back-arrow">&larr;</span> Back to Home
      </Link>

      {/* Header */}
      <div className="starter-kit-header">
        <h1>Your Starter Kit</h1>
        <p className="starter-kit-subtitle">
          Pre-loaded content that ships with every {BRANDING.productName} instance
          &mdash; templates, playbooks, reference data, and detection patterns ready
          to use from day one.
        </p>
      </div>

      {/* Card grid */}
      <div className="starter-kit-grid">
        {STARTER_KIT_CARDS.map((card) => {
          const isExpanded = expandedCard === card.id;

          return (
            <div
              key={card.id}
              className={`starter-kit-card${card.infoOnly ? ' info-only' : ''}`}
            >
              {/* Top row: title + count badge */}
              <div className="card-top">
                <h3>{card.title}</h3>
                <span className="card-count">{card.count}</span>
              </div>

              {/* Description */}
              <p className="card-description">{card.description}</p>

              {/* Action area */}
              {card.linkTo && (
                <button
                  type="button"
                  className="card-action-btn"
                  onClick={() => navigate(card.linkTo as string)}
                >
                  View &rarr;
                </button>
              )}

              {card.subLinks && (
                <>
                  <button
                    type="button"
                    className="card-expand-btn"
                    onClick={() => handleToggleExpand(card.id)}
                  >
                    {isExpanded ? 'Hide categories \u25be' : 'Browse categories \u25b8'}
                  </button>

                  {isExpanded && (
                    <div className="card-sub-links">
                      {card.subLinks.map((sub) => (
                        <Link key={sub.to} to={sub.to} className="card-sub-link">
                          <span>{sub.label}</span>
                          <span className="sub-link-count">{sub.count}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}

              {card.infoOnly && (
                <span className="card-info-tag">Runs automatically</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default StarterKitHub;
